"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { FilePopulated } from "@vita-learning/types";
import { getFilesKey, getFilesUrl, FILES_URL_SEGMENT, type FileQueryParams } from "~/shared/services/data/files/query";
import { createFile, deleteFile, fetchFiles, updateFile } from "~/shared/services/data/files/requests";

/**
 * Hook for fetching files. If `id` is provided in the params, will fetch a single file. Otherwise, will fetch all files (optionally filtered by `folderId`).
 *
 * @param {FileQueryParams} params the parameters for fetching files
 * @returns the file(s) along with mutation functions for creating, updating, and deleting files
 */
export const useFiles = t0 => {
  const $ = _c(23);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const params = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== params) {
    t2 = getFilesKey(params);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== params) {
    t3 = () => {
      if (params.disabled) {
        return [];
      }
      const url = getFilesUrl(params);
      return fetchFiles(url);
    };
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const queryResults = useSuspenseQuery(t4);
  let t5;
  if ($[9] !== queryClient) {
    t5 = () => queryClient.invalidateQueries({
      queryKey: [FILES_URL_SEGMENT]
    });
    $[9] = queryClient;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const revalidateAll = t5;
  let t6;
  if ($[11] !== revalidateAll) {
    t6 = {
      mutationFn: createFile,
      onSuccess: revalidateAll
    };
    $[11] = revalidateAll;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const create = useMutation(t6);
  let t7;
  if ($[13] !== revalidateAll) {
    t7 = {
      mutationFn: updateFile,
      onSuccess: revalidateAll
    };
    $[13] = revalidateAll;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const update = useMutation(t7);
  let t8;
  if ($[15] !== revalidateAll) {
    t8 = {
      mutationFn: deleteFile,
      onSuccess: revalidateAll
    };
    $[15] = revalidateAll;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const remove = useMutation(t8);
  let t9;
  if ($[17] !== create || $[18] !== queryResults || $[19] !== remove || $[20] !== revalidateAll || $[21] !== update) {
    t9 = {
      ...queryResults,
      create,
      update,
      remove,
      revalidateAll
    };
    $[17] = create;
    $[18] = queryResults;
    $[19] = remove;
    $[20] = revalidateAll;
    $[21] = update;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};